// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

export const oauthScopeSchema = z.enum([
  'hardfin:read',
  'hardfin:write',
  'offline_access',
]);

export type OAuthScope = z.infer<typeof oauthScopeSchema>;

const connectedApplicationSessionSchema = z.object({
  id: z.string(),
  deviceId: z.string().nullable(),
  signedInAt: dateTimeSchema,
  signInIpAddress: z.string().nullable(),
  signInOperatingSystem: z.string().nullable(),
  lastUsedAt: dateTimeSchema,
  lastUsedIpAddress: z.string().nullable(),
});

export type ConnectedApplicationSession = z.infer<
  typeof connectedApplicationSessionSchema
>;

const connectedApplicationSchema = z.object({
  key: z.string(),
  name: z.string(),
  scopes: z
    .array(oauthScopeSchema)
    .nullable()
    .transform((scopes) => scopes ?? []),
  connectedAt: dateTimeSchema,
  sessions: z
    .array(connectedApplicationSessionSchema)
    .nullable()
    .transform((sessions) => sessions ?? []),
});

export type ConnectedApplication = z.infer<typeof connectedApplicationSchema>;

export const connectedApplicationsResponseSchema = z.object({
  data: z.array(connectedApplicationSchema),
  metadata: responseMetadataSchema,
});

/** Requests the applications the signed-in person connected to their organization. */
export async function getConnectedApplications() {
  const path = '/v1/connected-application';
  const response = await api.get(path);

  return parseResponse(connectedApplicationsResponseSchema, response.data, {
    schemaName: 'ConnectedApplications',
    endpoint: path,
  });
}
